import type { SearchItem } from './types';

const RECENT_PREFIX = 'globalSearch.recent.';
const recentKey = (userId: number) => `${RECENT_PREFIX}v1.${userId}`;
const RECENT_MAX = 8;
// A note's title is its first line of clinical text: never kept on the device.
const NEVER_REMEMBERED = new Set(['note']);

export type RecentItem = Pick<SearchItem, 'id' | 'title' | 'sortAt' | 'meta' | 'inactive' | 'status'>;

export interface RecentEntry {
  entityType: string;
  item: RecentItem;
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode or blocked storage: remembering is a convenience only.
  }
}

export const readRecent = (userId: number): RecentEntry[] => {
  if (!(userId > 0)) return [];
  const value = read<unknown>(recentKey(userId), []);
  // Drops notes an older build may have stored.
  return Array.isArray(value) ? (value as RecentEntry[]).filter((r) => !NEVER_REMEMBERED.has(r?.entityType)) : [];
};

export function addRecent(userId: number, entityType: string, item: SearchItem): void {
  if (!(userId > 0) || NEVER_REMEMBERED.has(entityType)) return;
  const { id, title, sortAt, meta, inactive, status } = item;
  const rest = readRecent(userId).filter((r) => !(r.entityType === entityType && r.item.id === id));
  const entry: RecentEntry = { entityType, item: { id, title, sortAt, meta, inactive, status } };
  write(recentKey(userId), [entry, ...rest].slice(0, RECENT_MAX));
}

function removeRecentKeys(keep?: string): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(RECENT_PREFIX) && key !== keep) keys.push(key);
    }
    keys.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Blocked storage holds nothing to clear.
  }
}

/** Removes every user's recent results; called when the session ends. */
export const clearGlobalSearchPrefs = (): void => removeRecentKeys();

/** Removes recent results left by other users, e.g. after a session that ended without a logout. */
export const pruneOtherUsersRecent = (userId: number): void => removeRecentKeys(recentKey(userId));
